// Органы строки песни — ровно ДВА слота, всегда.
//
// Почему слот остаётся, когда поверхности нет. До волны E кнопка аудио у
// песни без записей не рисовалась вовсе, и колонка органов переставала быть
// колонкой: глаз, ведущий по ней сверху вниз, спотыкался на каждой такой
// строке. Пунктирный слот стоит на месте и НАЗЫВАЕТ отсутствие (`title`),
// вместо того чтобы умолчать его — неназванное отсутствие в этом проекте
// считается дефектом (см. app/renderer/ui/duplicate-menu-item.js).
//
// Слот аудио бывает ДВУХ видов, и видно это должно быть без нажатия. У песни
// с одной записью он ведёт в плеер; у песни с несколькими — раскрывает список
// версий прямо в строке. Волна E убрала пилюлю «N versions» и шеврон вместе
// со старой кнопкой, то есть оба признака «это раскроется», и слот у песни с
// одной записью и у песни с тремя стал выглядеть одинаково и нести одно и то
// же имя «Listen». Лидер жмёт иконку ноты, ожидая плеер, — раскрывается
// список; скринридер объявляет «Listen, кнопка» и о раскрытии молчит. Поэтому
// раскрывающий слот несёт `aria-expanded`/`aria-controls`, СВОЁ доступное имя
// и уголковый шеврон. Признак именно уголковый: ряд слотов обязан остаться
// 44×44 каждый — это и есть завоевание волны (имя песни перестало обрезаться),
// и широкая пилюля его бы отменила.
//
// Состояние раскрывашки принадлежит ВЛАДЕЛЬЦУ списка, не слоту: `onAudio`
// возвращает новое состояние, слот его отражает. Иначе у «раскрыт ли список»
// стало бы два хозяина — сам список и атрибут на кнопке.
//
// Модуль чистый: ни localStorage, ни i18n — подписи приходят параметрами,
// ровно как в sort-control.ts. Перевод и навигация остаются у library-view.
import { icon } from './icons.js';

export interface SongRowControlLabels {
  /** Доступное имя органа слов. */
  words: string;
  /** Доступное имя органа аудио, когда он ведёт в плеер. */
  audio: string;
  /** Доступное имя органа аудио, когда он раскрывает список записей. */
  audioVersions: string;
  /** Причина, по которой слот слов пуст. */
  noWords: string;
  /** Причина, по которой слот аудио пуст. */
  noAudio: string;
  /**
   * Единый сетлист, фаза 3 (гостевой экран, design gate): имя И подсказка
   * слота аудио, ЗАКРЫТОГО для гостя (запись есть на сервере, но доступ
   * `access.audio === false`). Слот не «пуст» — он есть и кликабелен (ведёт
   * на вход), просто с замком вместо ноты. Обязателен, только когда вызывающий
   * задаёт `audioLocked: true` в опциях.
   */
  audioLocked?: string;
}

export interface SongRowControlsOpts {
  hasWords: boolean;
  hasAudio: boolean;
  /** У песни больше одной записи: слот аудио раскрывает список, а не играет. */
  audioExpands?: boolean;
  /** id списка версий, которым управляет раскрывающий слот (`aria-controls`). */
  audioControls?: string;
  /**
   * Гость с закрытым аудио (единый сетлист, фаза 3): слот аудио рисуется с
   * замком вместо ноты — не пунктирным «отсутствием» и не обычной кнопкой
   * плеера. Имеет приоритет над `hasAudio`/`audioExpands`: тап всё равно ведёт
   * куда-то (обычно — на вход), а не молчит, поэтому это не то же самое, что
   * «слота нет». По умолчанию `false` — существующие вызовы не меняют вид.
   */
  audioLocked?: boolean;
  labels: SongRowControlLabels;
  onWords: () => void;
  /** Раскрывашка возвращает НОВОЕ состояние списка; вход в плеер — ничего.
   *  У замка (`audioLocked`) это обработчик «войти». */
  onAudio: () => void | boolean;
}

/** Слот, который РАСКРЫВАЕТ список: своё имя и id того, чем он управляет. */
interface SlotExpander { label: string; controls?: string | undefined }

function buildSlot(
  slot: 'words' | 'audio',
  present: boolean,
  label: string,
  reason: string,
  onClick: () => void | boolean,
  expander?: SlotExpander | undefined,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'row-slot';
  btn.dataset.slot = slot;
  btn.dataset.slotState = present ? 'present' : 'absent';
  const disclosure = present && expander !== undefined;
  btn.className = 'lib-slot' + (present ? '' : ' is-absent') + (disclosure ? ' is-expander' : '');
  btn.innerHTML = icon(slot === 'words' ? 'words' : 'music');
  if (!present) {
    btn.setAttribute('aria-label', reason);
    btn.setAttribute('aria-disabled', 'true');
    btn.title = reason;
    return btn;
  }
  if (!disclosure) {
    btn.setAttribute('aria-label', label);
    btn.addEventListener('click', () => { onClick(); });
    return btn;
  }
  btn.setAttribute('aria-label', expander.label);
  btn.setAttribute('aria-expanded', 'false');
  if (expander.controls) btn.setAttribute('aria-controls', expander.controls);
  // Видимый признак — уголковый шеврон ВНУТРИ слота: габарит 44×44 остаётся,
  // а «это раскроется» видно и глазом, не только скринридеру. Поворот на
  // раскрытии рисует CSS по `aria-expanded`, то есть по тому же признаку,
  // который читает скринридер, — двум состояниям один источник.
  const caret = document.createElement('span');
  caret.className = 'lib-slot-caret';
  caret.setAttribute('aria-hidden', 'true');
  caret.innerHTML = icon('chevronDown');
  btn.appendChild(caret);
  btn.addEventListener('click', () => {
    const open = onClick();
    if (typeof open === 'boolean') btn.setAttribute('aria-expanded', String(open));
  });
  return btn;
}

/**
 * Слот аудио, закрытого для гостя. Кликабелен (в отличие от `is-absent`
 * выше — там нажатие ничего не делает) и несёт замок ПОВЕРХ обычной иконки
 * ноты, а не вместо неё: узнаваемость «это слот аудио» остаётся, замок только
 * добавляет «но не тебе». `aria-disabled` НЕ ставится — слот действует.
 */
function buildLockedAudioSlot(label: string, onClick: () => void | boolean): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'row-slot';
  btn.dataset.slot = 'audio';
  btn.dataset.slotState = 'locked';
  btn.className = 'lib-slot is-locked';
  btn.innerHTML = icon('music');
  btn.setAttribute('aria-label', label);
  btn.title = label;
  const badge = document.createElement('span');
  badge.className = 'lib-slot-lock';
  badge.setAttribute('aria-hidden', 'true');
  badge.innerHTML = icon('lock');
  btn.appendChild(badge);
  btn.addEventListener('click', () => { onClick(); });
  return btn;
}

/** Пара органов строки. Не вставляет себя никуда — место выбирает вызывающий. */
export function buildSongRowControls(opts: SongRowControlsOpts): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lib-row-controls';
  wrap.dataset.role = 'song-row-controls';
  wrap.appendChild(buildSlot('words', opts.hasWords, opts.labels.words, opts.labels.noWords, opts.onWords));
  wrap.appendChild(
    opts.audioLocked
      ? buildLockedAudioSlot(opts.labels.audioLocked ?? opts.labels.audio, opts.onAudio)
      : buildSlot(
          'audio', opts.hasAudio, opts.labels.audio, opts.labels.noAudio, opts.onAudio,
          opts.audioExpands ? { label: opts.labels.audioVersions, controls: opts.audioControls } : undefined,
        ),
  );
  return wrap;
}
